'use client';
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { Input } from '@/components/ui/field';
import { PhoneInput } from '@/components/ui/phone-input';
import { requestPhoneInvitation, verifyPhoneInvitation } from '@/lib/actions';
import { checkPassword, isPasswordValid, RESET_RULE_IDS } from '@/lib/password-rules';
import { PasswordRulesChecklist, usePasswordRuleLabels } from '@/components/ui/password-rules-checklist';
import { Button, FormError } from './ui';

type Phase = 'phone' | 'verify';
const PENDING_INVITATION_KEY = 'luxmed-phone-invitation';

/** Invitation activation backed entirely by the LuxMed WhatsApp challenge.
 * Supabase receives the password and emits a session only after our code is consumed. */
export function PhoneAccountActivation() {
  const t = useTranslations('auth');
  const [phase, setPhase] = React.useState<Phase>('phone');
  const [phone, setPhone] = React.useState('');
  const [challengeId, setChallengeId] = React.useState('');
  const [code, setCode] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [pending, startTransition] = React.useTransition();
  // No `current`: the person activating an invitation has no account yet, so there is no
  // current password to differ from. Passing `current: ''` made `notCurrent` unsatisfiable
  // (the rule needs `current.length > 0`), which pinned `isPasswordValid` to false and made
  // the submit below return silently forever. Omitting the field is what the recovery
  // screens do, and it is what lets the rule pass.
  const rules = checkPassword({ next: password, confirm });
  const ruleLabels = usePasswordRuleLabels();

  // WhatsApp opens links in an in-app browser. Returning to the chat to copy the code can
  // destroy that tab, so keep the non-secret challenge metadata and resume when the invite
  // button is opened again. Passwords and codes never touch storage.
  React.useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(PENDING_INVITATION_KEY) ?? 'null') as {
        phone?: string;
        challengeId?: string;
      } | null;
      if (stored?.phone && stored.challengeId) {
        setPhone(stored.phone);
        setChallengeId(stored.challengeId);
        setPhase('verify');
      }
    } catch {
      localStorage.removeItem(PENDING_INVITATION_KEY);
    }
  }, []);

  const send = () => {
    setError(null);
    startTransition(async () => {
      const result = await requestPhoneInvitation(phone);
      if (!result.challengeId) {
        setError(t('phoneActivationError'));
        return;
      }
      setChallengeId(result.challengeId);
      setPhase('verify');
      localStorage.setItem(
        PENDING_INVITATION_KEY,
        JSON.stringify({ phone, challengeId: result.challengeId }),
      );
    });
  };

  const verify = (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d{4}$/.test(code) || !isPasswordValid({ next: password, confirm })) {
      // Never refuse in silence: a dead button teaches nothing. The checklist explains the
      // password rules, so the only failure it cannot show is the two fields disagreeing.
      setError(password !== confirm ? t('passwordMismatch') : t('phoneActivationError'));
      return;
    }
    setError(null);
    startTransition(async () => {
      const result = await verifyPhoneInvitation(challengeId, code, password);
      if (!result.ok) {
        setError(t('phoneActivationError'));
        return;
      }
      localStorage.removeItem(PENDING_INVITATION_KEY);
      window.location.assign('/app');
    });
  };

  if (phase === 'phone') {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">{t('phoneActivationBody')}</p>
        <PhoneInput value={phone} onChange={setPhone} aria-label={t('phonePlaceholder')} required />
        <FormError>{error}</FormError>
        <Button type="button" block size="lg" loading={pending} onClick={send}>
          {t('phoneActivationSend')}
        </Button>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={verify}>
      <p className="text-sm text-muted-foreground">{t('phoneActivationCodeBody')}</p>
      <Input
        value={code}
        onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 4))}
        inputMode="numeric"
        autoComplete="one-time-code"
        icon="shield-key"
        placeholder={t('mfaCodePlaceholder')}
        required
      />
      <Input
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
        placeholder={t('newPasswordPlaceholder')}
        required
      />
      <Input
        type="password"
        value={confirm}
        onChange={(event) => setConfirm(event.target.value)}
        autoComplete="new-password"
        placeholder={t('confirmPasswordPlaceholder')}
        required
      />
      <PasswordRulesChecklist
        results={rules}
        labels={ruleLabels}
        ids={RESET_RULE_IDS}
        show={password.length > 0}
      />
      <FormError>{error}</FormError>
      <div className="flex flex-col gap-2">
        <Button type="submit" block size="lg" loading={pending}>
          {t('phoneActivationContinue')}
        </Button>
        <Button type="button" variant="link" block disabled={pending} onClick={send}>
          {t('mfaResend')}
        </Button>
      </div>
    </form>
  );
}
